import { useState, useEffect, useMemo } from 'react';
import { observer, inject } from 'mobx-react';
import { Box, Checkbox, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel } from '@mui/material';
import { useSortableTableData } from '../../../components/table/hooks/useSortableTableData';
import { statementTablePaperSx, statementTableContainerSx, statementTableSx, statementTableHeaderSx } from '../../../components/table/mui-table-styles';
import { fetch } from '../../../utils/api-wrapper';
import InvoicePreview from '../../../components/Invoice-preview';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faSync } from '@fortawesome/free-solid-svg-icons';
import Modal from '../../../components/modal.js';
import toast from 'react-hot-toast';
import LoadingSpinner from '../../../components/loading-spinner/loading-spinner';
import FormTextField from '../../../components/form-textfield';

const columnNameKeys = {
  expense_preview: null,
  file_name: 'cc_statements.unmatched_expenses.column_names.file_name',
  user: 'cc_statements.unmatched_expenses.column_names.user',
  expense_date: 'cc_statements.unmatched_expenses.column_names.date',
  total_amount: 'cc_statements.unmatched_expenses.column_names.amount',
  restart_matching: 'cc_statements.unmatched_expenses.column_names.matching',
};

const UnmatchedExpensesTable = ({ data, unmatchedExpenses, refreshStatementData, isLoading, statementId }) => {
  const [selectedChecksums, setSelectedChecksums] = useState([]);
  const [showDeleteExpenseConfirmation, setShowDeleteExpenseConfirmation] = useState(false);
  const [fieldsAreUpdating, setFieldsAreUpdating] = useState(false);
  const [restartingChecksums, setRestartingChecksums] = useState(new Set());

  const prepareTableData = (expenses) => {
    const tableData = expenses.map((expense) => ({
      checksum: expense?.checksum,
      expense_preview: <InvoicePreview key={`${expense?.checksum}-${expense?.json?.Name}`} file_name={expense?.json?.Name} currentInvoice={null} />,
      file_name: expense?.original_file_name || expense?.name || 'N/A',
      total_amount: fieldsAreUpdating ? (
        <LoadingSpinner data-testid='loading-spinner' />
      ) : (
        <FormTextField
          format='money'
          value={expense?.json?.TotalWithTax ? `${expense?.json.TotalWithTax.toFixed(2)}` : '0.00'}
          validateChange={(val) => {
            const parsed = Number.parseFloat(val);
            if (!Number.isFinite(parsed)) return; // ignore invalid input
            updateExpense({ ...expense, json: { ...expense?.json, TotalWithTax: parsed } });
          }}
        />
      ),
      expense_date: fieldsAreUpdating ? (
        <LoadingSpinner data-testid='loading-spinner' />
      ) : (
        <FormTextField
          format='date'
          value={expense?.json?.InvoiceDate || ''}
          validateChange={(val) => {
            updateExpense({ ...expense, json: { ...expense?.json, InvoiceDate: val } });
          }}
        />
      ),
      user: (() => {
        const first = expense?.by_user?.first_name?.trim() || expense?.json?.by_user?.first_name?.trim();
        const last = expense?.by_user?.last_name?.trim() || expense?.json?.by_user?.last_name?.trim();
        const combined = [first, last].filter(Boolean).join(' ').trim();
        return combined || 'N/A';
      })(),
      restart_matching: (
        <button
          className='btn btn-sm btn-primary w-100 d-flex justify-content-center align-items-center'
          disabled={restartingChecksums.has(expense?.checksum)}
          onClick={() => restartMatching(expense?.checksum)}
        >
          {restartingChecksums.has(expense?.checksum) ? (
            <LoadingSpinner data-testid={`restart-spinner-${expense?._id}`} />
          ) : (
            <>
              <FontAwesomeIcon icon={faSync} className='mr-2' />
              {data.t.read('cc_statements.uncategorized_expenses.column_names.restart_matching')}
            </>
          )}
        </button>
      ),
      raw_expense: expense,
    }));

    return tableData;
  };

  const deleteSelectedExpenses = () => {
    const expense_checksums = selectedRows.map((exp) => exp.raw_expense.checksum);
    fetch(
      `/c_card/expenses/${statementId}`,
      'DELETE',
      { expense_checksums },
      (response) => {
        toast.success(data.t.read('cc_transactions.delete_successful') || 'Expenses deleted successfully');
        refreshStatementData();
        setSelectedChecksums([]);
      },
      (error) => {
        console.error('Error deleting expenses:', error);
        toast.error(data.t.read('cc_transactions.delete_failed') || 'Failed to delete expenses');
      }
    );
    setShowDeleteExpenseConfirmation(false);
  };

  const updateExpense = async (updatedExpense) => {
    setFieldsAreUpdating(true);

    updatedExpense.autoMatch = false;

    try {
      await fetch(
        `/c_card/expenses/${statementId}/${updatedExpense.checksum}`,
        'PUT',
        updatedExpense,
        () => {
          // Refresh statement data to get the latest expense data
          refreshStatementData();
        },
        (error) => {
          throw error;
        }
      );
    } catch {
      // Refresh on failure to ensure UI is in sync
      refreshStatementData();
    } finally {
      setFieldsAreUpdating(false);
    }
  };

  const restartMatching = async (expenseChecksum) => {
    // Add checksum to restarting set
    setRestartingChecksums((prev) => new Set([...prev, expenseChecksum]));

    const expenseChecksums = [expenseChecksum];

    try {
      await fetch(
        `/c_card/expenses/${statementId}/restart_matching`,
        'POST',
        { expense_checksums: expenseChecksums },
        () => {
          toast.success(data.t.read('cc_statements.restart_matching_successful') || 'Restarted matching successfully');
          refreshStatementData();
        },
        (error) => {
          throw error;
        }
      );
    } catch {
      toast.error(data.t.read('cc_statements.restart_matching_failed') || 'Failed to restart matching');
    } finally {
      // Remove checksum from restarting set
      setRestartingChecksums((prev) => {
        const next = new Set(prev);
        next.delete(expenseChecksum);
        return next;
      });
    }
  };

  const memoizedTableData = useMemo(() => prepareTableData(unmatchedExpenses || []), [unmatchedExpenses, restartingChecksums, fieldsAreUpdating]);

  const { sortedData, sortConfig, handleSort } = useSortableTableData(memoizedTableData);
  const columns = Object.entries(columnNameKeys);
  const selectedRows = memoizedTableData.filter((row) => selectedChecksums.includes(row.checksum));
  const allSelected = memoizedTableData.length > 0 && selectedRows.length === memoizedTableData.length;

  // Selection follows receipt checksums even when matching removes or reorders rows.
  useEffect(() => {
    setSelectedChecksums((checksums) => checksums.filter((checksum) => memoizedTableData.some((row) => row.checksum === checksum)));
  }, [memoizedTableData]);

  if (!unmatchedExpenses || unmatchedExpenses.length === 0) {
    return <></>;
  }

  return (
    <div>
      <div className='d-flex justify-content-between align-items-center mb-3 mt-2' style={{ minHeight: '30px' }}>
        <Box component='h2' sx={{ m: 0, fontSize: '18px', fontWeight: 500, color: '#20242a' }}>{data.t.read('cc_statements.unmatched_expenses.title')}</Box>
        {selectedRows && selectedRows.length > 0 && (
          <button className='btn btn-danger' onClick={() => setShowDeleteExpenseConfirmation(true)}>
            <FontAwesomeIcon icon={faTrash} className='mr-2' />
            {data.t.read('cc_transactions.delete_expenses')}
          </button>
        )}
      </div>
      <Paper elevation={0} sx={statementTablePaperSx}>
        <TableContainer sx={statementTableContainerSx}>
          <Table stickyHeader aria-label={data.t.read('cc_statements.unmatched_expenses.title')} aria-busy={isLoading || false} sx={statementTableSx}>
            <TableHead>
              <TableRow>
                <TableCell padding='checkbox' sx={{ ...statementTableHeaderSx, py: 0 }}>
                  <Checkbox
                    checked={allSelected}
                    indeterminate={selectedRows.length > 0 && !allSelected}
                    disabled={isLoading}
                    inputProps={{ 'aria-label': data.t.read('cc_statements.unmatched_expenses.select_all') }}
                    onChange={(event) => setSelectedChecksums(event.target.checked ? memoizedTableData.map((row) => row.checksum) : [])}
                  />
                </TableCell>
                {columns.map(([key, label]) => (
                  <TableCell key={key} sortDirection={sortConfig.key === key ? sortConfig.direction : false} sx={statementTableHeaderSx}>
                    {label && (
                      <TableSortLabel active={sortConfig.key === key} direction={sortConfig.key === key ? sortConfig.direction : 'asc'} onClick={() => handleSort(key)}>
                        {data.t.read(label)}
                      </TableSortLabel>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={columns.length + 1}>
                  <div className='d-flex justify-content-center'><LoadingSpinner /></div>
                </TableCell></TableRow>
              ) : sortedData.map((row) => (
                <TableRow
                  key={row.checksum}
                  hover
                  selected={selectedChecksums.includes(row.checksum)}
                  draggable
                  onDragStart={(event) => {
                    // Keep the existing transaction drop target's receipt payload unchanged.
                    event.dataTransfer.setData('application/json', JSON.stringify(row.raw_expense));
                    event.dataTransfer.effectAllowed = 'move';
                  }}
                >
                  <TableCell padding='checkbox'>
                    <Checkbox
                      checked={selectedChecksums.includes(row.checksum)}
                      inputProps={{ 'aria-label': `${data.t.read('select')} ${row.file_name}` }}
                      onChange={(event) => {
                        const checked = event.target.checked;
                        setSelectedChecksums((checksums) => checked ? [...checksums, row.checksum] : checksums.filter((checksum) => checksum !== row.checksum));
                      }}
                    />
                  </TableCell>
                  <TableCell>{row.expense_preview}</TableCell>
                  <TableCell>
                    <Box component='span' title={row.file_name} sx={{ display: 'block', minWidth: 0, maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.file_name}</Box>
                  </TableCell>
                  <TableCell>{row.user}</TableCell>
                  <TableCell>{row.expense_date}</TableCell>
                  <TableCell>
                    {row.total_amount}
                  </TableCell>
                  <TableCell>{row.restart_matching}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      {showDeleteExpenseConfirmation && (
        <Modal
          width='50vw'
          title={data.t.read('cc_transactions.delete_expenses')}
          cancelAction={() => setShowDeleteExpenseConfirmation(false)}
          okAction={deleteSelectedExpenses}
          okText={data.t.read('delete')}
          cancelText={data.t.read('cancel')}
        >
          <p>{`${data.t.read('cc_transactions.delete_expenses_confirm')} (${selectedRows.length})${data.t.read(
            'cc_transactions.delete_expenses_confirm2'
          )}`}</p>
        </Modal>
      )}

      <hr className='my-5' />
    </div>
  );
};

export default inject('data')(observer(UnmatchedExpensesTable));
